import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { formatMoney } from "../../lib/auth";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import { ExternalLink, RefreshCw, Copy, Trash2 } from "lucide-react";

const STATUS_COLOR = {
  approved: "bg-emerald-50 text-emerald-800 border-emerald-200",
  pending: "bg-amber-50 text-amber-800 border-amber-200",
  in_process: "bg-amber-50 text-amber-800 border-amber-200",
  rejected: "bg-red-50 text-red-800 border-red-200",
  refunded: "bg-blue-50 text-blue-800 border-blue-200",
};

const INTERNAL_LABEL = {
  not_started: "Não iniciado",
  preference_created: "Preferência criada",
  checkout_opened: "Checkout aberto",
  payment_created: "Pagamento criado",
  pending: "Aguardando",
  approved: "Aprovado",
  rejected: "Recusado",
  cancelled: "Cancelado",
  refunded: "Estornado",
  error: "Erro",
};

export default function AdminPayments() {
  const [rows, setRows] = useState([]);
  const [detail, setDetail] = useState(null);

  async function load() {
    const r = await api.get("/admin/payments");
    setRows(r.data);
  }
  useEffect(() => { load(); }, []);

  async function confirmManual(id) {
    if (!window.confirm("Confirmar manualmente este pagamento? A reserva será marcada como confirmada.")) return;
    try {
      await api.post(`/admin/payments/${id}/manual-confirm`);
      toast.success("Pagamento confirmado");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  }
  async function refund(id) {
    if (!window.confirm("Solicitar estorno junto ao Mercado Pago?")) return;
    try {
      await api.post(`/admin/payments/${id}/refund`);
      toast.success("Estorno solicitado");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  }
  async function del(id) {
    if (!window.confirm("Mover este pagamento para a Lixeira?")) return;
    try {
      await api.delete(`/admin/payments/${id}`);
      toast.success("Movido para Lixeira");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  }
  function copy(text, label = "Copiado") {
    navigator.clipboard.writeText(text || "");
    toast.success(label);
  }

  return (
    <div data-testid="admin-payments-page">
      <div className="mb-8">
        <span className="text-[11px] uppercase tracking-[0.28em] text-slate-500">Financeiro</span>
        <h1 className="text-3xl font-semibold text-[#0AA89C] mt-1">Pagamentos</h1>
        <p className="text-sm text-slate-500 mt-1">Confira, confirme manualmente e estorne pagamentos recebidos via Mercado Pago. Clique numa linha para ver o diagnóstico completo (preference_id, payment_id, status MP, webhook).</p>
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">
              <th className="px-4 py-3">Reserva</th>
              <th className="px-4 py-3">Hóspede</th>
              <th className="px-4 py-3">Método</th>
              <th className="px-4 py-3">Valor</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Ambiente</th>
              <th className="px-4 py-3 text-right">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((p) => {
              const method = p.payment_method_id || p.method || "—";
              const type = p.payment_type_id || "—";
              return (
                <tr key={p.id} data-testid={`payment-row-${p.id}`} className="cursor-pointer hover:bg-slate-50" onClick={() => setDetail(p)}>
                  <td className="px-4 py-3 font-mono text-xs">{p.reservation_code}</td>
                  <td className="px-4 py-3">{p.guest_name}</td>
                  <td className="px-4 py-3">
                    <div>{method}</div>
                    <div className="text-[11px] text-slate-500">{type}{p.installments ? ` · ${p.installments}x` : ""}</div>
                  </td>
                  <td className="px-4 py-3">{formatMoney(p.amount)}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 border rounded ${STATUS_COLOR[p.status] || "bg-slate-50 border-slate-200"}`}>{p.status}</span>
                    <div className="text-[11px] text-slate-500 mt-1">{INTERNAL_LABEL[p.internal_payment_status] || "—"}</div>
                  </td>
                  <td className="px-4 py-3 text-xs">
                    <div className="uppercase tracking-widest">{p.environment || "—"}</div>
                    <div className={`text-[11px] ${p.credential_kind === "sandbox" ? "text-amber-600" : "text-emerald-700"}`}>{p.credential_kind || "?"}{p.live_mode === false ? " · TEST" : p.live_mode === true ? " · LIVE" : ""}</div>
                  </td>
                  <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                    <div className="flex justify-end gap-1">
                      {p.status === "approved" && !p.manually_confirmed && (
                        <Button size="sm" variant="outline" onClick={() => confirmManual(p.id)} data-testid={`confirm-payment-${p.id}`}>Conferir</Button>
                      )}
                      {p.status === "approved" && p.mp_payment_id && (
                        <Button size="sm" variant="ghost" onClick={() => refund(p.id)} data-testid={`refund-payment-${p.id}`}>Estornar</Button>
                      )}
                      {p.status !== "approved" && (
                        <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50" onClick={() => del(p.id)} data-testid={`delete-payment-${p.id}`}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
            {rows.length === 0 && (
              <tr><td colSpan={7} className="text-center py-12 text-slate-500">Nenhum pagamento registrado ainda.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {detail && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setDetail(null)}>
          <div className="bg-white max-w-2xl w-full rounded-md shadow-lg p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="payment-detail">
            <div className="flex justify-between items-baseline mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.24em] text-slate-500">Diagnóstico do pagamento</div>
                <h2 className="text-xl font-semibold text-[#22302E]">{detail.reservation_code} · {detail.guest_name}</h2>
              </div>
              <button onClick={() => setDetail(null)} className="text-slate-400 hover:text-slate-700 text-2xl leading-none">×</button>
            </div>
            <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
              <Field label="Valor" value={formatMoney(detail.amount)} />
              <Field label="Método" value={`${detail.payment_method_id || detail.method || "—"} (${detail.payment_type_id || "—"})`} />
              <Field label="Status MP" value={detail.status} />
              <Field label="Status interno" value={INTERNAL_LABEL[detail.internal_payment_status] || "—"} />
              <Field label="mp_status" value={detail.mp_status || "—"} />
              <Field label="mp_status_detail" value={detail.mp_status_detail || "—"} />
              <Field label="Parcelas" value={detail.installments || "—"} />
              <Field label="Transaction amount" value={detail.transaction_amount ? formatMoney(detail.transaction_amount) : "—"} />
              <FieldCopy label="Preference ID" value={detail.mp_preference_id} onCopy={copy} />
              <FieldCopy label="Payment ID" value={detail.mp_payment_id} onCopy={copy} />
              <Field label="Collector ID" value={detail.collector_id || "—"} />
              <Field label="live_mode" value={String(detail.live_mode ?? "—")} />
              <Field label="Ambiente" value={detail.environment || "—"} />
              <Field label="Credencial" value={detail.credential_kind || "—"} />
              <Field label="Webhook recebido em" value={detail.webhook_received_at?.slice(0, 19).replace("T", " ") || "—"} />
              <Field label="Última sync" value={detail.last_sync_at?.slice(0, 19).replace("T", " ") || "—"} />
            </div>
            {(detail.raw_data?.preference?.init_point || detail.raw_data?.preference?.sandbox_init_point) && (
              <div className="mt-6 pt-4 border-t border-slate-100">
                <div className="text-[10px] uppercase tracking-[0.24em] text-slate-500 mb-2">Links do Checkout Pro</div>
                <div className="flex flex-wrap gap-2">
                  {detail.raw_data.preference.init_point && (
                    <a href={detail.raw_data.preference.init_point} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs px-3 py-1.5 border rounded border-slate-300 hover:border-[#0AA89C]">
                      <ExternalLink className="w-3 h-3" /> Abrir init_point
                    </a>
                  )}
                  {detail.raw_data.preference.sandbox_init_point && (
                    <a href={detail.raw_data.preference.sandbox_init_point} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs px-3 py-1.5 border rounded border-amber-300 text-amber-700 hover:border-amber-500">
                      <ExternalLink className="w-3 h-3" /> Abrir sandbox_init_point
                    </a>
                  )}
                  {detail.mp_payment_id && (
                    <a href={`https://www.mercadopago.com.br/activities/1/${detail.mp_payment_id}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs px-3 py-1.5 border rounded border-slate-300 hover:border-[#0AA89C]">
                      <ExternalLink className="w-3 h-3" /> Ver no Mercado Pago
                    </a>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.24em] text-slate-500">{label}</div>
      <div className="text-slate-700">{value}</div>
    </div>
  );
}
function FieldCopy({ label, value, onCopy }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.24em] text-slate-500">{label}</div>
      <div className="flex items-center gap-1">
        <code className="text-slate-700 text-xs break-all">{value || "—"}</code>
        {value && (
          <button onClick={() => onCopy(value, `${label} copiado`)} className="text-slate-400 hover:text-[#0AA89C]" title="Copiar">
            <Copy className="w-3 h-3" />
          </button>
        )}
      </div>
    </div>
  );
}
